// ============================================================================
// @jeraisonne/db — clue-hunt-source : helpers PURS d'aide au tuteur.
// ============================================================================
// Sert à l'éditeur ET à la modale de sélection texte : décompte les
// occurrences EXACTES d'un `source_text` dans les blocs sources (statement +
// statement_question + correction.answer_md des questions précédentes), pour
// détecter les cas ambigus (N > 1 → obligation de restreindre via
// source_block_id ou de raffiner le texte).
//
// La règle de matching MIROIRE `findClueByClick` côté player (trim + insensible
// à la casse). Si tu changes l'une, change l'autre.
// ============================================================================

export interface ClueHuntSourceCandidate {
  /** UUID du bloc (statement / statement_question / correction). */
  id: string;
  kind: 'statement' | 'statement_question' | 'correction';
  /** Texte brut du bloc (content_md / prompt_md / answer_md). */
  content_md: string;
  /** Libellé court pour l'UI (ex. « Énoncé 1 », « Question 2 »). */
  label: string;
}

export interface ClueOccurrence {
  /** Bloc source où l'occurrence a été trouvée. */
  block_id: string;
  block_kind: 'statement' | 'statement_question' | 'correction';
  block_label: string;
  /** Offset caractère dans `content_md` (avant normalisation). */
  offset: number;
}

/**
 * Normalise pour la comparaison : trim + lower + réduit les espaces internes
 * à un seul espace (le texte source peut contenir des sauts de ligne).
 */
export function normalizeClueMatch(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Renvoie toutes les occurrences de `needle` dans le pool de sources, avec
 * leurs métadonnées. Comparaison : trim + lower + espaces réduits (miroir
 * `normalizeClueMatch`). Renvoie [] si `needle` est vide (après trim).
 */
export function findClueOccurrences(
  needle: string,
  sources: readonly ClueHuntSourceCandidate[],
): ClueOccurrence[] {
  const n = normalizeClueMatch(needle);
  if (!n) return [];
  const out: ClueOccurrence[] = [];
  for (const s of sources) {
    const hay = normalizeClueMatch(s.content_md);
    let idx = 0;
    while (idx < hay.length) {
      const found = hay.indexOf(n, idx);
      if (found < 0) break;
      out.push({
        block_id: s.id,
        block_kind: s.kind,
        block_label: s.label,
        offset: found,
      });
      idx = found + n.length;
    }
  }
  return out;
}

/**
 * Verdict pour l'UI éditeur : pas d'occurrence → erreur, une → OK, plusieurs →
 * il faut restreindre (source_block_id) ou raffiner. Pure, sans I/O.
 */
export type ClueMatchVerdict =
  | { kind: 'none' }
  | { kind: 'unique'; occurrence: ClueOccurrence }
  | { kind: 'ambiguous'; occurrences: ClueOccurrence[] };

export function verifyClueSourceText(
  needle: string,
  sources: readonly ClueHuntSourceCandidate[],
  scopeBlockId?: string,
): ClueMatchVerdict {
  const pool = scopeBlockId ? sources.filter((s) => s.id === scopeBlockId) : sources;
  const occs = findClueOccurrences(needle, pool);
  if (occs.length === 0) return { kind: 'none' };
  if (occs.length === 1) return { kind: 'unique', occurrence: occs[0]! };
  return { kind: 'ambiguous', occurrences: occs };
}
